import { Component, Input, OnInit } from '@angular/core';
import { JiraIssue } from '../core/models/jira-issue.model';
import { JiraApiService } from '../core/services/jira-api.service';
import { FlagService } from '../core/services/flag.service';
import { WindowRefService } from '../core/services/windowRef.service';
import { HubstaffMeDTO } from '../core/models/hubstaff-me.model';
import { HubstaffApiService } from '../core/services/hubstaff-api.service';
import { SpinnerComponent } from '../shared/spinner/spinner.component';
import { NgIf } from '@angular/common';
import {
  HubstaffOrganizationDTO,
  HubstaffProjectDTO,
  HubstaffTaskDTO,
  HubstaffTimeEntryDTO,
  TimeEntryMappingDTO,
} from '../core/models/hubstaff-entities.model';
import { ActivitiesComponent, LinkTimeEntryEvent } from './activities/activities.component';
import { ChangedOrganizationEvent, SelectOrganizationComponent } from '../shared/select/select-organization.component';
import { ChangedProjectsEvent, SelectProjectComponent } from '../shared/select/select-project.component';
import { ChangedTasksEvent, SelectTaskComponent } from '../shared/select/select-task.component';

@Component({
  selector: 'app-issue-details',
  templateUrl: './issue-details.component.html',
  standalone: true,
  imports: [
    SpinnerComponent,
    NgIf,
    ActivitiesComponent,
    SelectOrganizationComponent,
    SelectProjectComponent,
    SelectTaskComponent,
  ],
})
export class IssueDetailsComponent implements OnInit {
  @Input() me?: HubstaffMeDTO;
  @Input() issueKey?: string;

  organizations: HubstaffOrganizationDTO[] = [];
  activeOrganization?: HubstaffOrganizationDTO;
  timeEntryMappings: TimeEntryMappingDTO[] = [];
  timeEntries: HubstaffTimeEntryDTO[] = [];
  projects: HubstaffProjectDTO[] = [];
  activeProject?: HubstaffProjectDTO;
  tasks: HubstaffTaskDTO[] = [];
  activeTask?: HubstaffTaskDTO;
  jiraIssue?: JiraIssue;
  loading = true;
  loadingEntries = false;

  constructor(
    private hubstaffApiService: HubstaffApiService,
    private jiraApiService: JiraApiService,
    private flagService: FlagService,
    private windowRefService: WindowRefService,
  ) {}

  async ngOnInit(): Promise<void> {
    await this.loadJiraIssue();
    await this.loadTimeEntryMappings();
    await this.loadOrganizations();
    this.loading = false;
    this.windowRefService.AP.resize('', 400);
  }

  async linkTimeEntry(event: LinkTimeEntryEvent): Promise<void> {
    event.btnEvent.startSpinner();
    try {
      if (event.timeEntry.tracked < 60) {
        this.flagService.warningMsg('Time entry duration should be more or equal to a minute');
        event.btnEvent.stopSpinner();

        return;
      }

      const entry = this.jiraApiService.prepareWorklogItem(event.timeEntry);
      const worklog = await this.jiraApiService.addWorklogItem(this.jiraIssue?.getKey() ?? 'no-issue-key', entry);
      await this.hubstaffApiService.addTimeEntryMapping(this.jiraIssue?.getKey() ?? '', event.timeEntry, worklog);

      event.timeEntry.jiraWorklogId = worklog.id;
      event.btnEvent.stopSpinner();
    } catch (error) {
      console.error('Cannot link entry due to: ', error);
      event.btnEvent.stopSpinner();
    }
  }

  async unlinkTimeEntry(event: LinkTimeEntryEvent): Promise<void> {
    event.btnEvent.startSpinner();
    try {
      await this.hubstaffApiService.deleteTimeEntryMapping(this.jiraIssue?.getKey() ?? 'no-key', event.timeEntry);
      await this.jiraApiService.deleteWorklogItem(
        this.jiraIssue?.getKey() ?? 'no-key',
        event.timeEntry.jiraWorklogId ?? 'no-worklog',
      );

      event.timeEntry.jiraWorklogId = undefined;
      event.btnEvent.stopSpinner();
    } catch (error) {
      console.error('Cannot unlink entry due to: ', error);
      event.btnEvent.stopSpinner();
    }
  }

  selectedTasksEventHandler(event: ChangedTasksEvent): void {
    if (event && event.selectedTasks && event.selectedTasks.length > 0) {
      this.activeTask = event.selectedTasks[0];
    } else {
      this.activeTask = undefined;
    }
    this.loadTimeEntries();
  }

  selectedProjectsEventHandler(event: ChangedProjectsEvent): void {
    this.activeTask = undefined;
    if (event && event.selectedProjects && event.selectedProjects.length > 0) {
      this.setActiveProject(event.selectedProjects[0]);
    } else {
      this.setActiveProject(undefined);
      this.timeEntries = [];
      this.tasks = [];
    }
  }

  selectedOrganizationsEventHandler(event: ChangedOrganizationEvent): void {
    if (event && event.selectedOrganization && event.selectedOrganization.length > 0) {
      this.setActiveOrganization(event.selectedOrganization[0]);
    }
  }

  private async loadOrganizations(): Promise<void> {
    try {
      this.organizations = await this.hubstaffApiService.organizations();
      if (this.organizations && this.organizations.length > 0) {
        this.setActiveOrganization(this.organizations[0]);
      }
    } catch (errorResponse) {
      console.error('Cannot load organizations due to: ', errorResponse);
    }
  }

  private async loadTasks(): Promise<void> {
    try {
      this.tasks = await this.hubstaffApiService.tasks(this.activeProject);
    } catch (errorResponse) {
      console.error('Cannot load tasks due to: ', errorResponse);
    }
  }

  private async setActiveProject(project?: HubstaffProjectDTO): Promise<void> {
    this.activeProject = project;

    if (project) {
      Promise.all([this.loadTimeEntries(), this.loadTasks()]).catch(error => {
        console.error('Cannot load tasks and entries due to: ', error);
      });
    }
  }

  private async setActiveOrganization(org: HubstaffOrganizationDTO): Promise<void> {
    this.loading = true;
    this.activeOrganization = org;

    // await this.loadTimeEntries();
    this.setActiveProject(undefined);
    this.timeEntries = [];
    this.tasks = [];
    this.activeTask = undefined;
    await this.loadProjects();
    this.loading = false;
  }

  private async loadProjects(): Promise<void> {
    if (!this.activeOrganization) return;

    try {
      this.projects = await this.hubstaffApiService.projects(this.activeOrganization.id);
    } catch (errorResponse) {
      console.error('Cannot load projects due to: ', errorResponse);
    }
  }

  private async loadTimeEntries(): Promise<void> {
    if (!this.activeOrganization) return;

    try {
      this.loadingEntries = true;
      const projectIds = [];
      const taskIds = [];

      if (this.activeProject) {
        projectIds.push(this.activeProject.id);
      }
      if (this.activeTask) {
        taskIds.push(this.activeTask.id);
      }
      const timeEntries = await this.hubstaffApiService.timeEntries(
        this.activeOrganization.id,
        this.me?.id ?? '',
        projectIds,
        taskIds,
      );

      timeEntries.sort((a, b) => {
        return (new Date(b.startsAt) as any) - (new Date(a.startsAt) as any);
      });

      this.timeEntries = timeEntries.map(timeEntry => {
        timeEntry.organizationName = this.activeOrganization?.name;

        const timeEntryMapping = this.timeEntryMappings.find(timeEntryMapping => {
          return timeEntryMapping.hubstaffEntryId.toString() === timeEntry.id.toString();
        });

        timeEntry.jiraWorklogId = timeEntryMapping ? timeEntryMapping.jiraWorklogId : undefined;

        return timeEntry;
      });
      this.loadingEntries = false;
    } catch (errorResponse) {
      this.loadingEntries = false;
      console.error('Cannot load time entries due to: ', errorResponse);
    }
  }

  private async loadTimeEntryMappings(): Promise<void> {
    try {
      this.timeEntryMappings = await this.hubstaffApiService.timeEntryMappings(this.jiraIssue?.getKey() ?? 'no-key');
    } catch (error) {
      console.error('Cannot load time entry mappings due to: ', error);
    }
  }

  private async loadJiraIssue(): Promise<void> {
    try {
      this.jiraIssue = await this.jiraApiService.issue(this.issueKey || '');
    } catch (error) {
      console.error('Cannot fetch issue due to: ', error);
    }
  }
}
